import { Trend } from '@/graphql';

type SentimentReport = {
	meanPersonalScore: number;
	meanTeamScore: number;
	meanCongregationalScore: number;
};

// Total predicted movement on the 1–5 scale below which a trend is considered flat.
export const FLAT_THRESHOLD = 0.25;

/**
 * Derives a Trend enum value from a series of feedback reports using OLS linear
 * regression over the averaged (personal + team + congregation) / 3 score.
 *
 * Returns Flat when:
 *   - fewer than 2 reports exist
 *   - the regression line's total movement across the dataset is < FLAT_THRESHOLD
 */
export function deriveTrend(
	reports: SentimentReport[],
	threshold = FLAT_THRESHOLD,
): Trend {
	const n = reports.length;
	if (n < 2) return Trend.Flat;

	const ys = reports.map(
		(r) => (r.meanPersonalScore + r.meanTeamScore + r.meanCongregationalScore) / 3,
	);

	const sumX = (n * (n - 1)) / 2;
	const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
	const sumY = ys.reduce((a, b) => a + b, 0);
	const sumXY = ys.reduce((acc, y, i) => acc + i * y, 0);

	const denom = n * sumX2 - sumX * sumX;
	if (denom === 0) return Trend.Flat;

	const slope = (n * sumXY - sumX * sumY) / denom;
	const totalMovement = Math.abs(slope * (n - 1));

	if (totalMovement < threshold) return Trend.Flat;
	return slope > 0 ? Trend.Up : Trend.Down;
}
